import type { WorkspaceState } from './types';
import { STORAGE_KEY } from '../config/appConfig';

const WORKSPACE_TTL_MS = 60 * 60 * 1000;

type StoredWorkspace = {
  savedAt: number;
  state: WorkspaceState;
};

export function loadWorkspace(): WorkspaceState | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as StoredWorkspace | WorkspaceState;

    // Old format support: remove it once so stale layouts do not survive forever.
    if (isWorkspaceState(parsed)) {
      localStorage.removeItem(STORAGE_KEY);
      return undefined;
    }

    if (!isStoredWorkspace(parsed)) {
      localStorage.removeItem(STORAGE_KEY);
      return undefined;
    }

    const expired = Date.now() - parsed.savedAt > WORKSPACE_TTL_MS;

    if (expired) {
      localStorage.removeItem(STORAGE_KEY);
      return undefined;
    }

    return parsed.state;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
}

export function saveWorkspace(state: WorkspaceState): void {
  const payload: StoredWorkspace = {
    savedAt: Date.now(),
    state
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function clearWorkspace(): void {
  localStorage.removeItem(STORAGE_KEY);
}

function isStoredWorkspace(value: unknown): value is StoredWorkspace {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as StoredWorkspace;

  return (
    typeof candidate.savedAt === 'number' &&
    Number.isFinite(candidate.savedAt) &&
    isWorkspaceState(candidate.state)
  );
}

function isWorkspaceState(value: unknown): value is WorkspaceState {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as WorkspaceState;

  return (
    Array.isArray(candidate.windows) &&
    typeof candidate.layouts === 'object' &&
    candidate.layouts !== null
  );
}